import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFlask, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { debounce } from 'lodash';
import parseLabValueData from './labValueData';
import './LabValueSidebar.css';

/**
 * Lab Value Sidebar component
 * @param {Object} props Component props
 */
const LabValueSidebar = ({
  isDarkMode,
  themeStyles,
  toggleSidebar,
  isVisible = true,
  onSIColumnChange
}) => {
  const [activeTab, setActiveTab] = useState('serum');
  const [showSIColumn, setShowSIColumn] = useState(false);

  const labData = React.useMemo(() => {
    return parseLabValueData();
  }, []); // Empty dependency array means this only runs once

  // console.log('LabValueSidebar - labData:', labData);
  // console.log('LabValueSidebar - activeTab:', activeTab);
  // console.log('LabValueSidebar - labData[activeTab]:', labData[activeTab]);

  const isMobile = window.innerWidth < 768;

  const tabs = [
    { key: 'serum', label: 'Serum', icon: faFlask },
    { key: 'cerebrospinal', label: 'CSF', icon: faFlask },
    { key: 'blood', label: 'Blood', icon: faFlask },
    { key: 'urine', label: 'Urine & BMI', icon: faFlask }
  ];

  const renderTestRow = (test, level = 0, parentPath = '') => {
    const indentStyle = { paddingLeft: `${level * 20}px` };
    const isParent = test.subTests && test.subTests.length > 0;

    // Create a unique key by combining parent path, test name, and level
    const uniqueKey = `${parentPath}-${test.name}-${level}`;

    return (
      <React.Fragment key={uniqueKey}>
        <tr className={`lab-value-row ${isParent ? 'parent-row' : ''} ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
          <td className={`test-name ${isDarkMode ? 'dark-mode' : 'light-mode'}`} style={indentStyle}>
            {level > 0 && <span className="hierarchy-bullet">• </span>}
            <span style={{ fontWeight: isParent ? 'bold' : 'normal' }}>
              {test.name || 'NO NAME'}
            </span>
          </td>
          <td className={`reference-range ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
            {test.referenceRange || ''}
          </td>
          {showSIColumn && (
            <td className={`si-interval ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
              {test.siInterval || ''}
            </td>
          )}
        </tr>
        {test.subTests && test.subTests.map((subTest, subIndex) =>
          renderTestRow(subTest, level + 1, `${uniqueKey}-sub-${subIndex}`)
        )}
      </React.Fragment>
    );
  };

  const sidebarStyle = {
    minWidth: isMobile ? 0 : 400,
    maxWidth: isMobile ? '100%' : 400,
    width: isMobile ? '100%' : '100%',
    background: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(249, 250, 251, 0.95)',
    borderRadius: '12px',
    marginLeft: 0,
    marginRight: 0,
    boxShadow: 'none',
    border: 'none',
    display: isVisible ? 'flex' : 'none',
    flexDirection: 'column',
    alignItems: 'stretch',
    zIndex: 2,
    position: 'relative',
    height: '100%',
    maxHeight: '100%',
    minHeight: 0,
    flex: '1 1 0',
    padding: 0,
    transition: 'all 0.3s ease',
    boxSizing: 'border-box',
    overflow: 'hidden',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)'
  };

  // Notify parent when SI column state changes
  React.useEffect(() => {
    if (onSIColumnChange) {
      onSIColumnChange(showSIColumn);
    }
  }, [showSIColumn, onSIColumnChange]);

  // Keep a ref-like variable for inline height override
  const [overrideHeight, setOverrideHeight] = useState(null);

  // Sync sidebar height with the question container when visible and on desktop
  React.useEffect(() => {
    if (!isVisible || window.innerWidth < 1024) {
      setOverrideHeight(null);
      return;
    }

    const QUESTION_CONTAINER_ID = 'examflip-question-container';
    const LAB_SIDEBAR_CONTAINER_ID = 'lab-value-sidebar-container';

    const syncHeights = () => {
      const questionContainer = document.getElementById(QUESTION_CONTAINER_ID);
      const labSidebarContainer = document.getElementById(LAB_SIDEBAR_CONTAINER_ID);
      
      if (questionContainer && labSidebarContainer) {
        const questionHeight = questionContainer.getBoundingClientRect().height;
        setOverrideHeight(`${questionHeight}px`);
        labSidebarContainer.style.height = `${questionHeight}px`;
        labSidebarContainer.style.maxHeight = `${questionHeight}px`;
      }
    };

    // Initial sync
    syncHeights();

    // Set up ResizeObserver to watch for question container size changes
    let resizeObserver = null;
    const questionContainer = document.getElementById(QUESTION_CONTAINER_ID);
    
    if (questionContainer) {
      try {
        // Debounce the syncHeights function to prevent rapid successive calls
        const debouncedSyncHeights = debounce(syncHeights, 100);
        
        resizeObserver = new ResizeObserver(() => {
          debouncedSyncHeights();
        });
        resizeObserver.observe(questionContainer);
        
        // Cleanup function that cancels pending debounced calls
        return () => {
          if (resizeObserver && questionContainer) {
            resizeObserver.unobserve(questionContainer);
          }
          debouncedSyncHeights.cancel();
        };
      } catch (err) {
        // Fallback to window resize if ResizeObserver not available
        const debouncedSyncHeights = debounce(syncHeights, 100);
        const handleResize = () => debouncedSyncHeights();
        window.addEventListener('resize', handleResize);
        return () => {
          window.removeEventListener('resize', handleResize);
          debouncedSyncHeights.cancel();
        };
      }
    }
  }, [isVisible]);

  const headerStyle = {
    fontWeight: 600,
    color: isDarkMode ? '#e2e8f0' : '#334155',
    padding: '16px 20px 12px 20px',
    fontSize: 16,
    textAlign: 'center',
    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    letterSpacing: '0.5px',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    background: 'transparent',
    borderRadius: '12px 12px 0 0',
    boxShadow: 'none',
    flexShrink: 0
  };

  const tabContainerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    minHeight: '44px',
    alignItems: 'stretch',
    padding: '0 12px 8px 12px',
    gap: '4px',
    justifyContent: 'space-between',
    background: 'transparent',
    boxShadow: 'none',
    flexShrink: 0
  };

  const tabStyle = (isActive) => ({
    padding: '8px 12px',
    cursor: 'pointer',
    background: isActive
      ? (isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)')
      : 'transparent',
    color: isActive
      ? (isDarkMode ? '#93c5fd' : '#1d4ed8')
      : (isDarkMode ? '#cbd5e1' : '#64748b'),
    borderBottom: isActive
      ? `2px solid ${isDarkMode ? '#60a5fa' : '#2563eb'}`
      : '2px solid transparent',
    fontSize: '13px',
    fontWeight: isActive ? 600 : 500,
    whiteSpace: 'nowrap',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    flex: '1 1 0',
    minWidth: '85px',
    maxWidth: 'none',
    borderRadius: '8px 8px 0 0',
    border: 'none',
    minHeight: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    boxShadow: 'none',
    transform: 'none'
  });

  const contentStyle = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '12px 16px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    minHeight: 0,
    maxHeight: 'none',
    width: '100%',
    boxSizing: 'border-box',
    background: 'transparent',
    borderRadius: '0 0 12px 12px',
    boxShadow: 'none'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
    tableLayout: 'fixed',
    minWidth: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    background: 'transparent',
    borderRadius: '8px',
    boxShadow: 'none',
    border: 'none'
  };

  // Enforce column widths/styles at runtime using !important so global compiled CSS
  // cannot override them. This runs whenever visibility or SI column toggles.
  React.useEffect(() => {
    try {
      const root = document.querySelector('.lab-value-sidebar');
      if (!root) return;
      const table = root.querySelector('table');
      if (!table) return;

      // Prefer colgroup if present
      const cols = table.querySelectorAll('col');
      if (cols && cols.length) {
        if (cols[0]) cols[0].style.setProperty('width', showSIColumn ? '45%' : '50%', 'important');
        if (cols[1]) cols[1].style.setProperty('width', showSIColumn ? '35%' : '40%', 'important');
        if (showSIColumn && cols[2]) cols[2].style.setProperty('width', '20%', 'important');
      }

      // Also enforce on header cells and reference cells as a fallback
      const ths = table.querySelectorAll('thead th');
      if (ths && ths.length) {
        if (ths[0]) ths[0].style.setProperty('width', showSIColumn ? '45%' : '50%', 'important');
        if (ths[1]) ths[1].style.setProperty('width', showSIColumn ? '35%' : '40%', 'important');
        if (showSIColumn && ths[2]) ths[2].style.setProperty('width', '20%', 'important');
      }

      // Ensure reference cells allow wrapping and proper alignment
      const refs = table.querySelectorAll('td.reference-range, th.reference-range');
      refs.forEach(r => {
        try {
          r.style.setProperty('white-space', 'normal', 'important');
          r.style.setProperty('overflow', 'visible', 'important');
          r.style.setProperty('text-overflow', 'clip', 'important');
          r.style.setProperty('text-align', 'right', 'important');
          r.style.setProperty('word-break', 'break-word', 'important');
          r.style.setProperty('hyphens', 'auto', 'important');
          r.style.setProperty('line-height', '1.4', 'important');
        } catch (e) {}
      });

      // Ensure SI interval cells allow wrapping too
      const siCells = table.querySelectorAll('td.si-interval, th.si-interval');
      siCells.forEach(s => {
        try {
          s.style.setProperty('white-space', 'normal', 'important');
          s.style.setProperty('overflow', 'visible', 'important');
          s.style.setProperty('text-overflow', 'clip', 'important');
          s.style.setProperty('text-align', 'right', 'important');
          s.style.setProperty('word-break', 'break-word', 'important');
          s.style.setProperty('hyphens', 'auto', 'important');
          s.style.setProperty('line-height', '1.4', 'important');
        } catch (e) {}
      });
    } catch (err) {
      // swallow DOM errors in environments without DOM
    }
  }, [showSIColumn, activeTab, isVisible]);

  const thStyle = {
    textAlign: 'right',
    padding: '12px 16px',
    fontWeight: 600,
    color: isDarkMode ? '#e2e8f0' : '#334155',
    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    background: 'transparent',
    fontSize: isMobile ? '12px' : '13px',
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    boxShadow: 'none'
  };

  const testHeaderStyle = {
    ...thStyle,
    textAlign: 'left' // Keep test column header left-aligned
  };

  const controlsStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 20px',
    borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    background: 'transparent',
    boxShadow: 'none',
    flexShrink: 0
  };

  return (
    <div className={`lab-value-sidebar question-navigation-sidebar lab-values-panel ${showSIColumn ? 'si-column-visible' : ''}`} style={{...sidebarStyle, height: overrideHeight || sidebarStyle.height}}>
      <div style={headerStyle}>
        {/* Close/Pin Handle */}
        <div
          className={`navigation-drag-handle ${isDarkMode ? 'dark-mode' : ''}`}
          onClick={() => {
            if (toggleSidebar) {
              toggleSidebar();
            }
          }}
          tabIndex={0}
          role="button"
          aria-label="Click to close sidebar"
          title="Click to close sidebar"
          style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            cursor: 'pointer'
          }}
        >
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: '12px' }} />
        </div>
        <FontAwesomeIcon icon={faFlask} />
        Lab Values
      </div>

      {/* Controls */}
      <div style={controlsStyle}>
        <span style={{ fontSize: '12px', fontWeight: 500, color: isDarkMode ? '#cbd5e1' : '#64748b' }}>
          Show SI Units
        </span>
        <button
          onClick={() => setShowSIColumn(!showSIColumn)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: isDarkMode ? '#cbd5e1' : '#64748b',
            padding: '4px',
            borderRadius: '4px',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'none';
          }}
        >
          <FontAwesomeIcon icon={showSIColumn ? faEye : faEyeSlash} style={{ fontSize: '14px' }} />
        </button>
      </div>

      {/* Tabs */}
      <div className="tab-row" style={tabContainerStyle}>
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={tabStyle(activeTab === tab.key)}
          >
            <FontAwesomeIcon icon={tab.icon} style={{ marginRight: '6px', fontSize: '14px' }} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={contentStyle}>
        <table style={tableStyle} className={isDarkMode ? 'dark-mode' : 'light-mode'}>
          {/* enforce column widths at markup level so layout stays predictable */}
          <colgroup>
            <col style={{ width: showSIColumn ? '45%' : '50%' }} />
            <col style={{ width: showSIColumn ? '35%' : '40%' }} />
            {showSIColumn && <col style={{ width: '20%' }} />}
          </colgroup>
          <thead>
            <tr>
              <th style={testHeaderStyle}>Test</th>
              <th style={thStyle}>Reference Range</th>
              {showSIColumn && <th style={thStyle}>SI Reference Interval</th>}
            </tr>
          </thead>
          <tbody className={isDarkMode ? 'dark-mode' : 'light-mode'}>
            {labData[activeTab] && labData[activeTab].length > 0 ? (
              labData[activeTab].map((test, index) => {
                return renderTestRow(test, 0, `root-${index}`);
              })
            ) : (
                <tr className={isDarkMode ? 'dark-mode' : 'light-mode'}>
                  <td colSpan={showSIColumn ? 3 : 2} style={{
                    textAlign: 'center',
                    padding: '20px',
                    color: isDarkMode ? '#cbd5e1' : '#64748b',
                    fontStyle: 'italic'
                  }}>
                    No data available for {tabs.find(tab => tab.key === activeTab)?.label}
                  </td>
                </tr>
              )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LabValueSidebar;